// src/api/adminSemantics.ts
/**
 * Frontend API client for v3 semantic layer admin operations.
 */
import type { MetricFormat } from "@/types/charts";
import { apiJson } from "./client";
import type { components } from "@verity/shared-types";

// --- Request Types ---

export type GenerateEmbeddingsRequest = {
  dataset_key: string;
};

export type ToggleFeatureFlagRequest = {
  data_source_id: string;
  agent_version: string | null;
};

// --- Response Types ---

export type GenerateEmbeddingsResponse = {
  dataset_key: string;
  items_embedded: number;
};

export type ToggleFeatureFlagResponse = {
  data_source_id: string;
  agent_version: string | null;
  config: Record<string, unknown>;
};

export type PackageSummary = {
  id: string;
  public_id: string;
  key: string;
  label: string;
};

export type LogicalDatasetSummary = {
  id: string;
  key: string;
  label: string;
  description: string;
  grain: string;
  created_at: string | null;
  dimensions_count?: number;
  metrics_count?: number;
  package_id?: string | null;
  package?: PackageSummary | null;
};

export type LogicalDimensionDetail = {
  key: string;
  label: string;
  role: string;
  description: string;
  synonyms: string[];
  member_count?: number;
  /** False when an admin hid it (only an automatic date part can be hidden). */
  is_active?: boolean;
  /** For an automatic date part: the key of the time dimension it is built
   *  from (plan 2026-10-16-every-time-dimension-carries-its-date-parts, D6). */
  date_part_of?: string | null;
  /** For an automatic date part: its unit. */
  date_part?: "year" | "quarter" | "month" | "week" | null;
};

// --- Dimension Member Types ---

export type DimensionMember = {
  id: string;
  value: string;
  label: string | null;
  description: string | null;
  synonyms: string[];
  sort_order: number;
  is_active: boolean;
};

// --- Metric filter (parity plan step 1.5; the backend's `MetricDSL.filter`) ---

export type MetricFilterOp =
  | "="
  | "!="
  | ">"
  | ">="
  | "<"
  | "<="
  | "IN"
  | "NOT IN"
  | "BETWEEN"
  | "IS NULL"
  | "IS NOT NULL";

export type MetricFilterGrain = "day" | "week" | "month" | "quarter" | "year";

export type MetricFilterValue = string | number | boolean;

/** One comparison on a dimension of the metric's own dataset. */
export type MetricFilterCondition = {
  dimension: string;
  op: MetricFilterOp;
  value?: MetricFilterValue | MetricFilterValue[] | null;
  /** Time dimensions only. */
  grain?: MetricFilterGrain | null;
  /** A dimension of a joined dataset: that dataset's key (plan 2026-10-10-
   *  semantic-entities-and-conversion). */
  via?: string | null;
};

/** A condition, or an and/or/not group of them (nested at most 5 levels). */
export type MetricFilterExpr =
  | MetricFilterCondition
  | { and: MetricFilterExpr[] }
  | { or: MetricFilterExpr[] }
  | { not: MetricFilterExpr };

/** The time grains a cumulative metric's window or period to date can use. */
export type CumulativeGrain = "day" | "week" | "month" | "quarter" | "year";

/** A cumulative metric's trailing window, e.g. the last 7 days. */
export type CumulativeWindow = { count: number; grain: CumulativeGrain };

/** What a conversion metric computes: the share of base events that
 *  convert (default), or the number of conversions. */
export type ConversionCalculation = "conversion_rate" | "conversions";

/** A conversion metric's definition as the admin API takes it: of the base
 *  events of an entity, the ones followed within the window by a conversion
 *  event of the same entity (plan 2026-10-10-semantic-entities-and-conversion,
 *  C1). `conversion_dataset` left out means this dataset. */
export type ConversionSpec = {
  entity: string;
  base_metric: string;
  conversion_metric: string;
  conversion_dataset?: string;
  window: CumulativeWindow;
  calculation?: ConversionCalculation;
};

/** A ratio side or formula operand: a metric key, or a key with its own filter. */
export type MetricRefValue = string | { metric: string; filter?: MetricFilterExpr | null; alias?: string | null };

export type LogicalMetricDetail = {
  key: string;
  label: string;
  description: string;
  synonyms: string[];
  // The metric's own number format (plan 2026-10-17 a KPI has a Format tab,
  // step 4): the renderer token (a stored `percent` reads as `percentage`),
  // decimals (null = Auto) and the affixes. Every answer carries them since
  // 2026-10-17; optional here so the fixtures that predate them still type,
  // and a reader treats absence as number / Auto / none.
  format?: MetricFormat;
  decimals?: number | null;
  format_prefix?: string | null;
  format_suffix?: string | null;
  metric_type: "aggregate" | "formula" | "count_distinct" | "ratio" | "cumulative" | "conversion";
  definition: {
    type: string;
    // Present (2) when the definition carries a filter, a fill or an object ref
    dsl_version?: number;
    // For aggregate metrics
    agg?: string;
    field_key?: string;
    // For formula metrics
    formula?: string;
    expression?: Record<string, unknown>;
    referenced_metrics?: string[];
    // For ratio metrics
    numerator?: MetricRefValue;
    denominator?: MetricRefValue;
    // For cumulative metrics: the metric it accumulates, and at most one of
    // a trailing window or a period to date (neither: running total)
    metric?: string;
    window?: CumulativeWindow | null;
    grain_to_date?: CumulativeGrain | null;
    // For conversion metrics (the window above is the conversion window)
    entity?: string;
    base_metric?: string;
    conversion_metric?: string;
    conversion_dataset?: string | null;
    calculation?: ConversionCalculation | null;
    // The metric's own filter (aggregate, count_distinct, ratio, cumulative)
    filter?: MetricFilterExpr | null;
    // What an empty period shows instead of no value (aggregate)
    fill_nulls_with?: number | null;
  };
};

/** A dimension of a dataset that a metric's own filter may name with
 *  `via`: the writer's own rule, never a date (plan 2026-10-10-semantic-
 *  entities-and-conversion, E4). */
export type JoinedDimensionDetail = {
  via: string;
  dataset_label: string;
  key: string;
  label: string;
  role: string;
};

export type LogicalDatasetDetail = LogicalDatasetSummary & {
  dimensions: LogicalDimensionDetail[];
  metrics: LogicalMetricDetail[];
  /** Absent from a server that predates it. */
  joined_dimensions?: JoinedDimensionDetail[];
  /** True when the dataset mirrors the customer's dbt project: its rows,
   *  date parts included, follow dbt and cannot be hidden here. */
  dbt_owned?: boolean;
};

// --- API Functions ---

/**
 * Generate embeddings for a logical dataset and its semantics.
 */
export async function generateEmbeddings(
  request: GenerateEmbeddingsRequest
): Promise<GenerateEmbeddingsResponse> {
  return apiJson<GenerateEmbeddingsResponse>("/admin/semantics/embeddings/generate", {
    method: "POST",
    body: JSON.stringify(request),
  });
}

/**
 * Toggle the agent_version feature flag on a data source.
 * Set to "v3-semantic" to enable the semantic pipeline.
 */
export async function toggleFeatureFlag(
  request: ToggleFeatureFlagRequest
): Promise<ToggleFeatureFlagResponse> {
  return apiJson<ToggleFeatureFlagResponse>("/admin/semantics/feature-flag/toggle", {
    method: "POST",
    body: JSON.stringify(request),
  });
}

/**
 * List all logical datasets for the current account.
 */
export async function listLogicalDatasets(): Promise<LogicalDatasetSummary[]> {
  return apiJson<LogicalDatasetSummary[]>("/admin/semantics/datasets");
}

/**
 * Get a logical dataset with its dimensions and metrics.
 */
export async function getLogicalDatasetDetails(
  datasetKey: string
): Promise<LogicalDatasetDetail> {
  return apiJson<LogicalDatasetDetail>(
    `/admin/semantics/datasets/${encodeURIComponent(datasetKey)}`
  );
}

/**
 * Delete a logical dataset and all related data.
 */
export async function deleteLogicalDataset(
  datasetKey: string
): Promise<{ status: string; dataset_key: string }> {
  return apiJson<{ status: string; dataset_key: string }>(
    `/admin/semantics/datasets/${encodeURIComponent(datasetKey)}`,
    { method: "DELETE" }
  );
}

// --- Dataset CRUD ---

export type CreateDatasetRequest = {
  key: string;
  label: string;
  description?: string;
  grain?: string;
};

export type UpdateDatasetRequest = {
  label?: string;
  description?: string;
  grain?: string;
};

export async function createLogicalDataset(
  request: CreateDatasetRequest
): Promise<LogicalDatasetSummary> {
  return apiJson<LogicalDatasetSummary>("/admin/semantics/datasets", {
    method: "POST",
    body: JSON.stringify(request),
  });
}

export async function updateLogicalDataset(
  datasetKey: string,
  request: UpdateDatasetRequest
): Promise<LogicalDatasetSummary> {
  return apiJson<LogicalDatasetSummary>(
    `/admin/semantics/datasets/${encodeURIComponent(datasetKey)}`,
    {
      method: "PUT",
      body: JSON.stringify(request),
    }
  );
}

// --- Dimension CRUD ---

export type CreateDimensionRequest = {
  key: string;
  label: string;
  role?: string;
  description?: string;
  synonyms?: string[];
};

export type UpdateDimensionRequest = {
  label?: string;
  role?: string;
  description?: string;
  synonyms?: string[];
  /** Hide (false) or show (true): an automatic date part only. */
  is_active?: boolean;
};

export async function createDimension(
  datasetKey: string,
  request: CreateDimensionRequest
): Promise<LogicalDimensionDetail> {
  return apiJson<LogicalDimensionDetail>(
    `/admin/semantics/datasets/${encodeURIComponent(datasetKey)}/dimensions`,
    {
      method: "POST",
      body: JSON.stringify(request),
    }
  );
}

export async function updateDimension(
  datasetKey: string,
  dimensionKey: string,
  request: UpdateDimensionRequest
): Promise<LogicalDimensionDetail> {
  return apiJson<LogicalDimensionDetail>(
    `/admin/semantics/datasets/${encodeURIComponent(datasetKey)}/dimensions/${encodeURIComponent(dimensionKey)}`,
    {
      method: "PUT",
      body: JSON.stringify(request),
    }
  );
}

export async function deleteDimension(
  datasetKey: string,
  dimensionKey: string
): Promise<{ status: string; dimension_key: string }> {
  return apiJson<{ status: string; dimension_key: string }>(
    `/admin/semantics/datasets/${encodeURIComponent(datasetKey)}/dimensions/${encodeURIComponent(dimensionKey)}`,
    { method: "DELETE" }
  );
}

// --- Dimension Member CRUD ---

export type CreateMemberRequest = {
  value: string;
  label?: string;
  description?: string;
  synonyms?: string[];
};

export type UpdateMemberRequest = {
  label?: string;
  description?: string;
  synonyms?: string[];
  is_active?: boolean;
};

export type BulkCreateMembersRequest = {
  members: CreateMemberRequest[];
};

export type BulkCreateMembersResponse = {
  created: number;
  skipped: number;
  total: number;
};

export async function listMembers(
  datasetKey: string,
  dimensionKey: string,
  includeInactive = false
): Promise<DimensionMember[]> {
  const params = includeInactive ? "?include_inactive=true" : "";
  return apiJson<DimensionMember[]>(
    `/admin/semantics/datasets/${encodeURIComponent(datasetKey)}/dimensions/${encodeURIComponent(dimensionKey)}/members${params}`
  );
}

export async function createMember(
  datasetKey: string,
  dimensionKey: string,
  request: CreateMemberRequest
): Promise<DimensionMember> {
  return apiJson<DimensionMember>(
    `/admin/semantics/datasets/${encodeURIComponent(datasetKey)}/dimensions/${encodeURIComponent(dimensionKey)}/members`,
    {
      method: "POST",
      body: JSON.stringify(request),
    }
  );
}

export async function bulkCreateMembers(
  datasetKey: string,
  dimensionKey: string,
  request: BulkCreateMembersRequest
): Promise<BulkCreateMembersResponse> {
  return apiJson<BulkCreateMembersResponse>(
    `/admin/semantics/datasets/${encodeURIComponent(datasetKey)}/dimensions/${encodeURIComponent(dimensionKey)}/members/bulk`,
    {
      method: "POST",
      body: JSON.stringify(request),
    }
  );
}

export async function updateMember(
  datasetKey: string,
  dimensionKey: string,
  memberId: string,
  request: UpdateMemberRequest
): Promise<DimensionMember> {
  return apiJson<DimensionMember>(
    `/admin/semantics/datasets/${encodeURIComponent(datasetKey)}/dimensions/${encodeURIComponent(dimensionKey)}/members/${encodeURIComponent(memberId)}`,
    {
      method: "PUT",
      body: JSON.stringify(request),
    }
  );
}

export async function deleteMember(
  datasetKey: string,
  dimensionKey: string,
  memberId: string
): Promise<{ status: string; member_id: string }> {
  return apiJson<{ status: string; member_id: string }>(
    `/admin/semantics/datasets/${encodeURIComponent(datasetKey)}/dimensions/${encodeURIComponent(dimensionKey)}/members/${encodeURIComponent(memberId)}`,
    { method: "DELETE" }
  );
}

// --- Metric CRUD ---

export type CreateMetricRequest = {
  key: string;
  label: string;
  // For aggregate metrics (provide field_key)
  field_key?: string;
  aggregation?: string;
  // For calculated metrics (provide formula)
  formula?: string;
  // For ratio metrics (metric keys of the same dataset)
  numerator?: string;
  denominator?: string;
  // For cumulative metrics (a metric key of the same dataset), with at most
  // one of window or grain_to_date; neither means a running total
  cumulative_of?: string;
  window?: CumulativeWindow;
  grain_to_date?: Exclude<CumulativeGrain, "day">;
  // For conversion metrics: the whole definition
  conversion?: ConversionSpec;
  // The metric's own filter (aggregate, ratio and cumulative metrics)
  filter?: MetricFilterExpr;
  // What an empty period shows instead of no value (aggregate metrics)
  fill_nulls_with?: number;
  // Common fields
  description?: string;
  synonyms?: string[];
  // The number format (plan 2026-10-17 a KPI has a Format tab, step 4);
  // left out: number, Auto, no affixes.
  format?: MetricFormat;
  decimals?: number | null;
  format_prefix?: string | null;
  format_suffix?: string | null;
};

export type UpdateMetricRequest = {
  label?: string;
  // For aggregate metrics
  field_key?: string;
  aggregation?: string;
  // For calculated metrics
  formula?: string;
  // Left out: keep the stored value. null: remove it.
  filter?: MetricFilterExpr | null;
  fill_nulls_with?: number | null;
  // Cumulative metrics only (the base metric cannot change)
  window?: CumulativeWindow | null;
  grain_to_date?: Exclude<CumulativeGrain, "day"> | null;
  // Conversion metrics only: replaces the whole conversion definition
  conversion?: ConversionSpec;
  // Common fields
  description?: string;
  synonyms?: string[];
  // The number format (step 4). Left out: keep the stored value; null on
  // decimals / an affix: clear it (Auto / none).
  format?: MetricFormat;
  decimals?: number | null;
  format_prefix?: string | null;
  format_suffix?: string | null;
};

export async function createMetric(
  datasetKey: string,
  request: CreateMetricRequest
): Promise<LogicalMetricDetail> {
  return apiJson<LogicalMetricDetail>(
    `/admin/semantics/datasets/${encodeURIComponent(datasetKey)}/metrics`,
    {
      method: "POST",
      body: JSON.stringify(request),
    }
  );
}

export async function updateMetric(
  datasetKey: string,
  metricKey: string,
  request: UpdateMetricRequest
): Promise<LogicalMetricDetail> {
  return apiJson<LogicalMetricDetail>(
    `/admin/semantics/datasets/${encodeURIComponent(datasetKey)}/metrics/${encodeURIComponent(metricKey)}`,
    {
      method: "PUT",
      body: JSON.stringify(request),
    }
  );
}

export async function deleteMetric(
  datasetKey: string,
  metricKey: string
): Promise<{ status: string; metric_key: string }> {
  return apiJson<{ status: string; metric_key: string }>(
    `/admin/semantics/datasets/${encodeURIComponent(datasetKey)}/metrics/${encodeURIComponent(metricKey)}`,
    { method: "DELETE" }
  );
}

// =============================================================================
// Customer metrics (plan 2026-10-13 dashboard calculated metrics, D6)
// =============================================================================

/** A customer metric of the account: account-level ("available everywhere")
 *  or dashboard-level (waiting to be promoted). `dashboard_id`/`dashboard_name`
 *  are set only for a dashboard-level metric whose dashboard the admin may open. */
export type AdminCustomMetric = components["schemas"]["AdminCustomMetricDTO"];

export async function listCustomMetrics(): Promise<AdminCustomMetric[]> {
  const body = await apiJson<components["schemas"]["AdminCustomMetricsResponse"]>(
    "/admin/semantics/custom-metrics",
  );
  return body.metrics;
}

/** Make a dashboard-level metric available everywhere. 409 with a dict
 *  detail (`operand_not_promoted` + `metrics`, or `already_account_level`). */
export async function promoteCustomMetric(
  metricId: string,
  /** The revision the caller read; the backend refuses (409) if it changed. */
  ifMatch?: string,
): Promise<AdminCustomMetric> {
  return apiJson<AdminCustomMetric>(
    `/admin/semantics/custom-metrics/${encodeURIComponent(metricId)}/promote`,
    ifMatch !== undefined
      ? { method: "POST", body: JSON.stringify({ if_match: ifMatch }) }
      : { method: "POST" },
  );
}

// =============================================================================
// Custom Semantic Packages
// =============================================================================

export type CustomPackageDimension = {
  key: string;
  label: string;
  role: string;
  description: string | null;
  synonyms: string[];
  members?: Array<{
    id: string;
    value: string;
    label: string | null;
    synonyms: string[];
  }>;
};

export type CustomPackageMetric = {
  key: string;
  label: string;
  description: string | null;
  metric_type: string;
  synonyms: string[];
  definition?: Record<string, unknown>;
};

export type CustomPackageDataset = {
  id: string;
  key: string;
  label: string;
  description: string | null;
  dataset_type: "fact" | "dimension";
  grain: string;
  dimensions_count: number;
  metrics_count: number;
  dimensions: CustomPackageDimension[];
  metrics: CustomPackageMetric[];
};

export type CustomPackageSummary = {
  id: string;
  public_id: string;
  key: string;
  label: string;
  description: string | null;
  origin: string;
  created_from: string;
  current_version: number;
  created_at: string | null;
  datasets_count: number;
  datasets: CustomPackageDataset[];
};

export type CustomPackageDetail = CustomPackageSummary;

/**
 * List all custom semantic packages for the current account.
 */
export async function listCustomPackages(): Promise<CustomPackageSummary[]> {
  return apiJson<CustomPackageSummary[]>("/admin/semantics/packages");
}

/**
 * Get a custom semantic package with full details including dimension members.
 */
export async function getCustomPackageDetails(
  packageId: string
): Promise<CustomPackageDetail> {
  return apiJson<CustomPackageDetail>(
    `/admin/semantics/packages/${encodeURIComponent(packageId)}`
  );
}

export type DeletePackageResponse = {
  status: string;
  package_id: string;
  package_key: string;
  datasets_deleted: number;
};

/**
 * Delete a custom semantic package and all its datasets.
 */
export async function deleteCustomPackage(
  packageId: string
): Promise<DeletePackageResponse> {
  return apiJson<DeletePackageResponse>(
    `/admin/semantics/packages/${encodeURIComponent(packageId)}`,
    { method: "DELETE" }
  );
}
